import { httpClient } from '@/shared/lib/http-client';
import { API_ENDPOINTS } from '@/shared/config/api';
import type {
  PriceCategoriesResponse,
  PriceCategoryResponse,
  PriceItemsResponse,
  PriceItemResponse,
  PriceCategoryMutationResponse,
  PriceItemMutationResponse,
  CreatePriceCategoryData,
  UpdatePriceCategoryData,
  CreatePriceItemData,
  UpdatePriceItemData,
  PriceSearchParams,
  PriceItemSortOrderUpdate
} from '@/shared/types';

/**
 * API сервис для управления ценами и услугами
 * Содержит методы для CRUD операций с категориями цен и услугами
 */
export const pricesApi = {
  // === Категории цен ===

  /**
   * Получить список всех категорий
   * @param params - Параметры поиска и фильтрации
   * @returns Ответ со списком категорий
   */
  async getAllCategories(
    params?: Pick<PriceSearchParams, 'includeInactive'>
  ): Promise<PriceCategoriesResponse> {
    const response = await httpClient.get<PriceCategoriesResponse>(
      API_ENDPOINTS.PRICES.CATEGORIES.LIST,
      { params }
    );
    return response.data;
  },

  /**
   * Получить категорию по ID
   * @param id - ID категории
   * @returns Ответ с данными категории
   */
  async getCategoryById(id: number): Promise<PriceCategoryResponse> {
    const response = await httpClient.get<PriceCategoryResponse>(
      API_ENDPOINTS.PRICES.CATEGORIES.DETAIL(id)
    );
    return response.data;
  },

  /**
   * Создать новую категорию
   * @param data - Данные для создания категории
   * @returns Ответ с созданной категорией
   */
  async createCategory(
    data: CreatePriceCategoryData
  ): Promise<PriceCategoryMutationResponse> {
    const response = await httpClient.post<PriceCategoryMutationResponse>(
      API_ENDPOINTS.PRICES.CATEGORIES.CREATE,
      data
    );
    return response.data;
  },

  /**
   * Обновить категорию
   * @param id - ID категории
   * @param data - Данные для обновления
   * @returns Ответ с обновленной категорией
   */
  async updateCategory(
    id: number,
    data: UpdatePriceCategoryData
  ): Promise<PriceCategoryMutationResponse> {
    const response = await httpClient.patch<PriceCategoryMutationResponse>(
      API_ENDPOINTS.PRICES.CATEGORIES.UPDATE(id),
      data
    );
    return response.data;
  },

  /**
   * Обновить статус активности категории
   * @param id - ID категории
   * @param isActive - Новый статус активности
   * @returns Ответ с обновленной категорией
   */
  async updateCategoryStatus(
    id: number,
    isActive: boolean
  ): Promise<PriceCategoryMutationResponse> {
    const response = await httpClient.patch<PriceCategoryMutationResponse>(
      API_ENDPOINTS.PRICES.CATEGORIES.UPDATE_STATUS(id),
      { isActive }
    );
    return response.data;
  },

  // === Услуги ===

  /**
   * Получить список всех услуг
   * @param params - Параметры поиска и фильтрации
   * @returns Ответ со списком услуг
   */
  async getAllItems(params?: PriceSearchParams): Promise<PriceItemsResponse> {
    const response = await httpClient.get<PriceItemsResponse>(
      API_ENDPOINTS.PRICES.ITEMS.LIST,
      { params }
    );
    return response.data;
  },

  /**
   * Получить услугу по ID
   * @param id - ID услуги
   * @returns Ответ с данными услуги
   */
  async getItemById(id: number): Promise<PriceItemResponse> {
    const response = await httpClient.get<PriceItemResponse>(
      API_ENDPOINTS.PRICES.ITEMS.DETAIL(id)
    );
    return response.data;
  },

  /**
   * Создать новую услугу
   * @param data - Данные для создания услуги
   * @returns Ответ с созданной услугой
   */
  async createItem(
    data: CreatePriceItemData
  ): Promise<PriceItemMutationResponse> {
    const response = await httpClient.post<PriceItemMutationResponse>(
      API_ENDPOINTS.PRICES.ITEMS.CREATE,
      data
    );
    return response.data;
  },

  /**
   * Обновить услугу
   * @param id - ID услуги
   * @param data - Данные для обновления
   * @returns Ответ с обновленной услугой
   */
  async updateItem(
    id: number,
    data: UpdatePriceItemData
  ): Promise<PriceItemMutationResponse> {
    const response = await httpClient.patch<PriceItemMutationResponse>(
      API_ENDPOINTS.PRICES.ITEMS.UPDATE(id),
      data
    );
    return response.data;
  },

  /**
   * Обновить статус активности услуги
   * @param id - ID услуги
   * @param isActive - Новый статус активности
   * @returns Ответ с обновленной услугой
   */
  async updateItemStatus(
    id: number,
    isActive: boolean
  ): Promise<PriceItemMutationResponse> {
    const response = await httpClient.patch<PriceItemMutationResponse>(
      API_ENDPOINTS.PRICES.ITEMS.UPDATE_STATUS(id),
      { isActive }
    );
    return response.data;
  },

  /**
   * Массовое обновление порядка сортировки услуг
   * @param updates - Массив обновлений с ID услуги и новым sortOrder
   * @returns Ответ с результатом обновления
   */
  async updateItemsSortOrder(updates: PriceItemSortOrderUpdate[]): Promise<{
    success: boolean;
    updatedCount: number;
    items: { id: number; sortOrder: number }[];
  }> {
    const response = await httpClient.patch<{
      success: boolean;
      updatedCount: number;
      items: { id: number; sortOrder: number }[];
    }>(API_ENDPOINTS.PRICES.ITEMS.UPDATE_SORT_ORDER, { updates });
    return response.data;
  }
};
